/**
 * User accessibility preferences — the single source of truth.
 *
 * Preferences are applied by setting a class / `data-*` attributes / CSS custom
 * properties on the <html> element (see `applyPrefs`). CSS in `index.css` reacts
 * to those hooks. React owns `#root`, NOT `<html>`, so driving these attributes
 * imperatively never causes a hydration mismatch with the pre-rendered (SSG)
 * markup — and the same hooks are set pre-paint by the inline script in
 * `index.html` to avoid a flash of unstyled content.
 *
 * IMPORTANT: the inline no-FOUC script in `index.html` mirrors `applyPrefs` +
 * `loadPrefs` in plain JS. Keep the two in sync (same STORAGE_KEY, same hooks).
 */

export type ThemePref = "light" | "dark" | "system";

/* DISABLED: accent colour picker. Uncomment this to bring it back (see also the matching blocks in index.html, a11yPrefs.ts, AccessibilityBar.tsx, accessibilityBar.test.tsx).

/** Accent colour. "teal" is the brand default; the others are visitor choices. * /
export type PalettePref = "teal" | "blue" | "violet" | "green";

/**
 * Accent colours offered in the accessibility bar. `swatch` / `swatchDark` are
 * the light- and dark-mode primary, used only to paint the picker dot. The real tokens live in index.css
 * (html[data-palette=...]); every palette was checked for WCAG AA contrast in
 * light, dark and (via the high-contrast override) high-contrast modes.
 * /
export const PALETTES: ReadonlyArray<{ id: PalettePref; label: string; swatch: string; swatchDark: string }> = [
  { id: "teal", label: "Teal", swatch: "hsl(185 45% 25%)", swatchDark: "hsl(185 45% 40%)" },
  { id: "blue", label: "Blue", swatch: "hsl(217 62% 35%)", swatchDark: "hsl(217 62% 62%)" },
  { id: "violet", label: "Violet", swatch: "hsl(264 48% 41%)", swatchDark: "hsl(264 48% 66%)" },
  { id: "green", label: "Green", swatch: "hsl(152 52% 23%)", swatchDark: "hsl(152 52% 42%)" },
];
*/

export interface A11yPrefs {
  /** Colour theme. "system" follows the OS `prefers-color-scheme`. */
  theme: ThemePref;
  // DISABLED: accent colour picker.
  // /** Accent colour. */
  // palette: PalettePref;
  /** High-contrast token overrides. */
  contrast: boolean;
  /** Desaturate page content. */
  grayscale: boolean;
  /** Invert page colours (images are re-inverted to look normal). */
  invert: boolean;
  /** Underline every link. */
  underlineLinks: boolean;
  /** Enlarge the mouse cursor. */
  largeCursor: boolean;
  /** Force-disable animations/transitions. */
  reduceMotion: boolean;
  /** WCAG 1.4.12 text spacing (line/letter/word). */
  textSpacing: boolean;
  /** Root font-size multiplier (A- / A / A+). */
  fontScale: number;
}

export const STORAGE_KEY = "ab-a11y-prefs";

/** Allowed font-scale steps for the A- / A / A+ control. */
export const FONT_SCALE_STEPS = [0.85, 1, 1.15, 1.3, 1.5] as const;

export const DEFAULT_PREFS: A11yPrefs = {
  theme: "system",
  // palette: "teal", // DISABLED: accent colour picker
  contrast: false,
  grayscale: false,
  invert: false,
  underlineLinks: false,
  largeCursor: false,
  reduceMotion: false,
  textSpacing: false,
  fontScale: 1,
};

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Whether the effective theme is dark, resolving "system". */
export function isDark(prefs: A11yPrefs): boolean {
  return prefs.theme === "dark" || (prefs.theme === "system" && systemPrefersDark());
}

function setFlag(html: HTMLElement, name: string, on: boolean) {
  if (on) html.setAttribute(name, "true");
  else html.removeAttribute(name);
}

/** Apply every preference to an <html> element. Idempotent. */
export function applyPrefs(html: HTMLElement, prefs: A11yPrefs): void {
  html.classList.toggle("dark", isDark(prefs));
  // DISABLED: accent colour picker
  // if (prefs.palette !== "teal") html.setAttribute("data-palette", prefs.palette);
  // else html.removeAttribute("data-palette");
  if (prefs.contrast) html.setAttribute("data-contrast", "high");
  else html.removeAttribute("data-contrast");
  setFlag(html, "data-grayscale", prefs.grayscale);
  setFlag(html, "data-invert", prefs.invert);
  setFlag(html, "data-underline-links", prefs.underlineLinks);
  setFlag(html, "data-large-cursor", prefs.largeCursor);
  setFlag(html, "data-reduce-motion", prefs.reduceMotion);
  setFlag(html, "data-text-spacing", prefs.textSpacing);
  html.style.setProperty("--a11y-font-scale", String(prefs.fontScale));
}

/** Read saved prefs (client-only), merged over defaults. Safe on the server. */
export function loadPrefs(): A11yPrefs {
  if (typeof window === "undefined" || !window.localStorage) return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<A11yPrefs>;
    return { ...DEFAULT_PREFS, ...parsed };
    // DISABLED: accent colour picker. When re-enabling, replace the line above with:
    // const merged = { ...DEFAULT_PREFS, ...parsed };
    // // Ignore an unknown saved value (old or hand-edited storage).
    // if (!PALETTES.some((x) => x.id === merged.palette)) merged.palette = "teal";
    // return merged;
  } catch {
    return DEFAULT_PREFS;
  }
}

/** Persist prefs (client-only, best-effort). */
export function savePrefs(prefs: A11yPrefs): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / privacy mode — ignore */
  }
}
